import { Component, computed, inject, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { Location } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Perfil } from 'src/app/models/Perfil';
import { Usuario } from 'src/app/models/Usuario';
import { UsuarioService } from 'src/app/services/usuario.service';
import { DomSanitizer } from '@angular/platform-browser';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource, Photo } from '@capacitor/camera';
import { API_URI } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { TipoDocumento } from 'src/app/models/TipoDocumento';
import * as bootstrap from 'bootstrap';
import { Cliente } from 'src/app/models/Cliente';
import { LoadingService } from 'src/app/services/loading.service';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, firstValueFrom, map, Observable, of, Subscription, switchMap } from 'rxjs';

@Component({
    selector: 'app-usuario',
    templateUrl: './usuario.component.html',
    styleUrls: ['./usuario.component.css'],
    standalone: false
})

export class UsuarioComponent implements OnInit, OnDestroy {
  private _authService = inject(AuthService);
  private _usuarioService = inject(UsuarioService);
  private _loadingService = inject(LoadingService);
  private router = inject(Router);
  private _location = inject(Location);
  private _fb = inject(FormBuilder);
  private sanitizer = inject(DomSanitizer);
  private aRoute = inject(ActivatedRoute);

  // estado
  error   = signal<string>('');
  loading = computed(() => this._loadingService.isLoading());

  id?: string;
  operacion = 'Agregar ';
  botonOperacion = 'Agregar ';

  perfiles: Perfil[] = [{ id: 0, x_descripcion: 'Seleccione', b_activo: true }];
  tiposDocumentos = computed<TipoDocumento[]>(() => {
    const base = this._usuarioService.tiposDocumentos() ?? [];
    return [{ id: 0, x_descripcion: 'Seleccione', b_activo: true }, ...base];
  });

  // forms
  moreUsuario!: FormGroup;
  myAccountIdentidad!: FormGroup;
  
  // imágenes y previews
  x_img_avatar?: File;
  preview_img_avatar = signal<string>('');
  preview_img_avatar_large = signal<string>('');
  x_img_dni_frente?: File;
  preview_img_dni_frente = signal<string>('');
  x_img_dni_dorso?: File;
  preview_img_dni_dorso = signal<string>('');

  d_fecha_creacion?: Date;
  d_fecha_modificacion?: Date;
  x_modificado_por?: string;
  fieldTextType = false;
  showList: 'perfil' | 'detalle' = 'perfil';
  
  // máscara y asociadoCardio
  maskDocumento = signal<string>('00000000'); // DNI por defecto (8 dígitos)
  asociadoCardio = signal<boolean>(false);
  isCheckingDni = signal<boolean>(false);   // spinner local
  private asociadoPorDni = false;
  maskSocio = signal<string>('00000000'); // 8 caracteres
  isCheckingSocio = signal<boolean>(false);   // spinner local
  private asociadoPorSocio = false;

  // modal
  private modalInstance?: any;
  private subs = new Subscription();

  private originalEmail: string = '';  // ← guardar email real del usuario al editar

  constructor(){
      this.createForms();
      this.id = this.aRoute.snapshot.paramMap.get('id') ?? '';
  }

  async ngOnInit(): Promise<void> {
    // Detectar cuando el usuario presiona "atrás" y cerrar el modal si está abierto
    this._location.subscribe(() => {
      const modalEl = document.getElementById('modalPreviewImg');
      if (modalEl && this.modalInstance) this.modalInstance.hide();
    });
    document.documentElement.scrollTop = 0;

    // Si necesitás (re)cargar usuario/cliente desde backend, hacelo bajo el loader:
    await this._loadingService.run(async () => {
      try {
        // Asegura bootstrap de sesión (idempotente, ok en web/nativo)
        await this._authService.ensureSession();

        this.perfiles = (this._usuarioService.perfiles ?? []).filter((p: Perfil) => p.id === 1 || p.id === 2); // 1 - ADMINISTRADOR || 2 - CLIENTE
        // Si hay id => edición
        if(this.id){
          // Es editar
          this.operacion = "Editar ";
          this.botonOperacion = "Actualizar ";
          // Bloquear email al editar
          this.moreUsuario.controls['email'].disable({ emitEvent: false }); // bloqueo visual

          // Cargar Usuario y Cliente en paralelo
          const [u, c] = await Promise.all([
            firstValueFrom(this._usuarioService.getUsuarioXId(this.id)),
            firstValueFrom(this._usuarioService.getClienteXId(this.id)),
          ]);

          // Si no existe el usuario, no podés editar
          if (!u) {
            this.error.set('El usuario no existe o fue eliminado.');
            this.router.navigate(['/more-usuarios']);
            return;
          }

          this.originalEmail = (u?.x_email ?? '').trim().toLowerCase();  // ← guardo el email original

          // PERFIL (Usuario)
          this.moreUsuario.patchValue({
            perfil: u?.id_perfil ?? 0,
            email:  this.originalEmail, // muestro, pero no lo voy a usar para enviar
            activo: !!u?.b_activo
          });
          if (u?.x_img_avatar) {
            this.preview_img_avatar.set(`${API_URI}uploads/images/small-${u.x_img_avatar}`);
            this.preview_img_avatar_large.set(`${API_URI}uploads/images/large-${u.x_img_avatar}`);
          }
          this.d_fecha_creacion = u?.d_fecha_creacion ?? undefined;
          this.d_fecha_modificacion = u?.d_fecha_modificacion ?? undefined;
          this.x_modificado_por = u?.x_modificado_por ?? undefined;

          // DETALLE (Cliente)
          // Importante: fijamos tipo_documento y la máscara ANTES de cargar
          // nro_documento. El cambio de [mask] recién llega al input en el
          // próximo ciclo de detección de cambios de Angular (no es sincrónico
          // con el .set() del signal) — por eso el patchValue de nro_documento
          // va en un setTimeout: si se hace en el mismo tick, ngx-mask todavía
          // tiene puesta la máscara vieja de DNI (8 dígitos) y trunca un
          // CUIT/CUIL (11 dígitos), aunque en la base el dato esté completo.
          this.myAccountIdentidad.patchValue({
            nombre:   c?.x_nombre ?? '',
            apellido: c?.x_apellido ?? '',
            telefono: c?.x_telefono ?? '',
            tipo_documento: u?.id_tipo_documento ?? 0,
          });
          this.actualizarMascara();
          setTimeout(() => {
            this.myAccountIdentidad.patchValue({
              nro_documento:       u?.x_nro_documento ?? '',
              telefono_emergencia: u?.x_telefono_emergencia ?? '',
              nro_socio:           u?.x_nro_socio ?? '',
            });
          });

          if (c?.x_img_dni_frente) this.preview_img_dni_frente.set(`${API_URI}uploads/images/${c.x_img_dni_frente}`);
          if (c?.x_img_dni_dorso)  this.preview_img_dni_dorso.set(`${API_URI}uploads/images/${c.x_img_dni_dorso}`);
        }
        // escuchar cambios de tipo_documento para máscara
        this.myAccountIdentidad.get('tipo_documento')!.valueChanges.subscribe(() => this.actualizarMascara());
      } catch (e: any) {
        console.error(e);
        this.error = e?.message || 'No se pudo cargar tu cuenta.';
        this.router.navigate(['/sign-in']);
      }
    });

    // === Debounce + spinner local para DNI ===
    const dniCtrl = this.myAccountIdentidad.get('nro_documento')!;
    const sub = dniCtrl.valueChanges.pipe(
      map(v => (v ?? '').toString()),
      map(v => v.replace(/\D/g, '')),              // solo dígitos
      filter(v => v.length >= 7),                  // umbral mínimo
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(dni => {
        this.isCheckingDni.set(true);
        return this._usuarioService.checkAsociadoXDni(dni).pipe(
          catchError(() => of({ habilitado: false } as any)),
          finalize(() => this.isCheckingDni.set(false))
        );
      })
    ).subscribe(res => {
      this.asociadoPorDni = !!res?.habilitado;
      this.asociadoCardio.set(this.asociadoPorDni || this.asociadoPorSocio);
      this.actualizarValidacionTelefonoEmergencia();
    });
    this.subs.add(sub);

    // Disparo inicial si ya había DNI
    const dniInicial = this.myAccountIdentidad.get('nro_documento')!.value;
    if (dniInicial) dniCtrl.setValue(dniInicial, { emitEvent: true });

    // === Debounce + spinner local para Nro. Socio ===
    const socioCtrl = this.myAccountIdentidad.get('nro_socio')!;
    const socioSub = socioCtrl.valueChanges.pipe(
      map(v => (v ?? '').toString()),
      map(v => v.replace(/\D/g, '')),
      filter(v => v.length >= 4),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(nroSocio => {
        this.isCheckingSocio.set(true);
        return this._usuarioService.checkAsociadoXNroSocio(nroSocio).pipe(
          catchError(() => of({ habilitado: false } as any)),
          finalize(() => this.isCheckingSocio.set(false))
        );
      })
    ).subscribe(res => {
      this.asociadoPorSocio = !!res?.habilitado;
      this.asociadoCardio.set(this.asociadoPorDni || this.asociadoPorSocio);
      this.actualizarValidacionTelefonoEmergencia();
    });
    this.subs.add(socioSub);

    // Disparo inicial si ya había Nro. Socio
    const socioInicial = this.myAccountIdentidad.get('nro_socio')!.value;
    if (socioInicial) socioCtrl.setValue(socioInicial, { emitEvent: true });
  }

  ngOnDestroy(): void {
    if (this.modalInstance) this.modalInstance.hide();
    if (this.modalConfirmarEliminarInstance) this.modalConfirmarEliminarInstance.hide();
    this.subs.unsubscribe();
  }

  // forms
  private createForms() {
    this.moreUsuario = this._fb.group({
      perfil: [2, Validators.required], // 2 - CLIENTE
      email: ['', [Validators.required, Validators.email]],
      password: [''],
      repetir_password: [''],
      activo: [true, Validators.required]
    });

    this.myAccountIdentidad = this._fb.group({
      nombre: [''],
      apellido: [''],
      telefono: ['', Validators.pattern(/^[0-9]*$/)],
      tipo_documento: [''],
      nro_documento: ['', [Validators.maxLength(13)]],
      telefono_emergencia: ['', Validators.pattern(/^[0-9]*$/)],
      nro_socio: ['', [Validators.minLength(4), Validators.maxLength(8)]],
    });
  }

  // -------- Solo números en teléfono / contacto de emergencia --------
  soloNumeros(controlName: 'telefono' | 'telefono_emergencia', event: Event) {
    const input = event.target as HTMLInputElement;
    const filtrado = input.value.replace(/\D/g, '');
    if (filtrado !== input.value) {
      this.myAccountIdentidad.get(controlName)?.setValue(filtrado);
    }
  }

  // -------- Modal foto perfil --------
  openModal() {
    const modalEl = document.getElementById('modalPreviewImg');
    if (modalEl) {
      this.modalInstance = new (bootstrap as any).Modal(modalEl);
      this.modalInstance.show();
    }
  }

  // -------- Máscara según tipo documento --------
  actualizarMascara() {
    const tipo = this.myAccountIdentidad.value.tipo_documento;
    if (tipo === 1 || tipo === 2) { // 1 - CUIT / 2 - CUIL
      this.maskDocumento.set('00-00000000-0'); // CUIT / CUIL con formato 99-99999999-9
    } else {
      this.maskDocumento.set('00000000'); // DNI (8 dígitos)
    }
  }

  // ====== Guardar USUARIO (perfil) ======
  async saveUsuario(){
    // Evita doble submit si ya está cargando
    if (this.loading()) return;

    this.error.set('');

    const v = this.moreUsuario.getRawValue();

    if (!v.perfil) { this.error.set('Debe seleccionar un Perfil'); return; }
    if (!v.email?.trim()) { this.error.set('Debe completar un Email'); return; }
    if (v.password !== v.repetir_password) { this.error.set('Los password no coinciden'); return; }

    if(v.password != '' && v.repetir_password != ''){
      if(v.password == ''){
        this.error.set('Debe completar un Password');
        return;
      }
      if(v.repetir_password == ''){
        this.error.set('Debe completar Repetir Password');
        return;
      }
    }
    // Respeta validaciones de template/reactivas
    if (this.moreUsuario.invalid) return;

    const isEdit = !!this.id;

    const usuario: Usuario = {
      id_perfil: v.perfil,
      x_email: isEdit ? this.originalEmail : v.email.trim().toLowerCase(), // ← en edición, ignora el form
      x_password: v.password,
      b_activo: !!v.activo
    };

    await this._loadingService.run(async () => {
      try {
        // 1) Subir avatar si corresponde (esperar antes de continuar)
        if (this.x_img_avatar) {
          await firstValueFrom(this._usuarioService.updateUsuarioImgAvatar(this.x_img_avatar, usuario.x_email!));
          // Si necesitas el nombre devuelto: const img = resp?.imagen;
        }
        // 2) Guardar datos
          await this.updateDatos(usuario);
      } catch (e) {
        console.error(e);
        this.error.set('Error al guardar el usuario');
      }
    });
  }

  async updateDatos(usuario: Usuario): Promise<void> {
    const isEdit = this.id !== '' && this.id != null;
    try {
      if (isEdit) {
        usuario.id = this.id!;
        await firstValueFrom(this._usuarioService.updateUsuarioFirebase(usuario));
      } else {
        await firstValueFrom(this._usuarioService.createUsuarioFirebase(usuario));
      }

      await this.router.navigate(['/more-usuarios']);
    } catch (e) {
      this.error.set(isEdit
        ? 'Error al actualizar el Usuario'
        : 'Error al agregar el Usuario');
      console.error(e);
      // Propagamos para que quien llame (saveUsuario) también pueda manejarlo si quiere
      throw e;
    }
  }

  // ====== Guardar DETALLE (cliente) ======
  async saveUsuarioDetalle(){
    // Evita doble submit si ya está cargando
    if (this.loading()) return;
    this.error.set('');

    // Tomo valores del form (getRawValue evita nulls “raros”)
    const v = this.myAccountIdentidad.getRawValue();

    if (v.telefono && !/^[0-9]*$/.test(v.telefono)) {
      this.error.set('El Teléfono debe contener solo números');
      return;
    }
    if (v.telefono_emergencia && !/^[0-9]*$/.test(v.telefono_emergencia)) {
      this.error.set('El Contacto de Emergencia debe contener solo números');
      return;
    }

    // validaciones rápidas del form
    if (this.myAccountIdentidad.invalid) {
      this.myAccountIdentidad.markAllAsTouched();
      this.error.set('Revisá los campos requeridos.');
      return;
    }

    const nroDoc = (v.nro_documento ?? '').toString();
    if (nroDoc && nroDoc.length >= 13) {
      this.error.set('El número de documento no puede tener más de 13 caracteres');
      return;
    }

    // Construyo el payload
    const cliente: Cliente = {
      id: this.id!,
      x_apellido: v.apellido ?? '',
      x_nombre: v.nombre ?? '',
      x_telefono: v.telefono ?? '',
    };

    const usuario: Usuario = {
      id: this.id!,
      id_tipo_documento:
        !v.tipo_documento || Number(v.tipo_documento) === 0
          ? null
          : Number(v.tipo_documento),
      x_nro_documento: v.nro_documento?.trim() || null,
      x_telefono_emergencia: v.telefono_emergencia?.trim() || null,
      x_nro_socio: v.nro_socio?.trim() || null,
    };

    await this._loadingService.run(async () => {
      try {
        // Subidas de imágenes en paralelo (si hay)
        const uploads: Promise<unknown>[] = [];
        if (this.x_img_dni_frente) {
          uploads.push(
            firstValueFrom(this._usuarioService.updateClienteImgDniFrente(this.x_img_dni_frente, cliente.id!))
          );
        }
        if (this.x_img_dni_dorso) {
          uploads.push(
            firstValueFrom(this._usuarioService.updateClienteImgDniDorso(this.x_img_dni_dorso, cliente.id!))
          );
        }
        if (uploads.length) {
          await Promise.all(uploads);
        }

        // Ahora sí, actualizo datos del cliente y usuario
        await this.updateDatosDetalle(cliente, usuario);
      } catch (e) {
        this.error.set('Error al actualizar el usuario');
        console.error(e);
        // no relanzo: el spinner igual se corta por LoadingService
      }
    });
  }

  async updateDatosDetalle(cliente: Cliente, usuario: Usuario): Promise<void> {
    try {
      await firstValueFrom(this._usuarioService.updateCliente(cliente));
      await firstValueFrom(this._usuarioService.updateUsuario(usuario));

      await this.router.navigate(['/more-usuarios']);
    } catch (e) {
      this.error.set('Error al actualizar el usuario');
      console.error(e);
      throw e;
    }
  }

  private actualizarValidacionTelefonoEmergencia() {
    // Condicionalmente agregar o eliminar validación para teléfono de emergencia
    // (el patrón de solo-números se mantiene siempre)
    const ctrl = this.myAccountIdentidad.get('telefono_emergencia');
    if (!ctrl) return;
    const validators = this.asociadoCardio()
      ? [Validators.required, Validators.pattern(/^[0-9]*$/)]
      : [Validators.pattern(/^[0-9]*$/)];
    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity();
  }

  // -------- Handlers de archivos y previews --------
  private async pickPhoto(fileName: string): Promise<{ file: File; dataUrl: string }> {
    // En web conviene Photos; en nativo Prompt (permite Cámara o Galería)
    const source = Capacitor.getPlatform() === 'web' ? CameraSource.Photos : CameraSource.Prompt;

    const photo: Photo = await Camera.getPhoto({
      source,
      // URI es más eficiente en memoria; luego lo convertimos a blob->File
      resultType: CameraResultType.Uri,
      quality: 85,
      allowEditing: false,
      correctOrientation: true,
      promptLabelHeader: 'Seleccionar imagen',
      promptLabelPhoto: 'Galería',
      promptLabelPicture: 'Cámara',
    });

    // Asegurar un webPath o dataUrl/base64
    const file = await this.photoToFile(photo, fileName);
    const dataUrl = await this.fileToDataUrl(file);
    return { file, dataUrl };
  }

  /** Convierte Photo de Capacitor a File */
  private async photoToFile(photo: Photo, fileName: string): Promise<File> {
    let blob: Blob | null = null;

    if (photo.webPath) {
      const res = await fetch(photo.webPath);
      blob = await res.blob();
    } else if (photo.dataUrl) {
      const res = await fetch(photo.dataUrl);
      blob = await res.blob();
    } else if (photo.base64String) {
      const byteString = atob(photo.base64String);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      blob = new Blob([ab], { type: 'image/jpeg' });
    }

    if (!blob) throw new Error('No se pudo leer la imagen.');
    return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
  }

  /** Convierte File a DataURL (para preview) */
  private async fileToDataUrl(file: File): Promise<string> {
    return await new Promise<string>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => resolve('');
      reader.readAsDataURL(file);
    });
  }

  // ==== Handlers de cada imagen usando los helpers ====

  async capturarFileImgAvatar(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('avatar.jpg');
      this.x_img_avatar = file;
      this.preview_img_avatar.set(dataUrl);
      this.preview_img_avatar_large.set(dataUrl);
    } catch (err) {
      console.error('Error al capturar avatar:', err);
    }
  }

  async capturarFileImgDniFrente(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('dni-frente.jpg');
      this.x_img_dni_frente = file;
      this.preview_img_dni_frente.set(dataUrl);
    } catch (err) {
      console.error('Error al capturar DNI frente:', err);
    }
  }

  async capturarFileImgDniDorso(): Promise<void> {
    try {
      const { file, dataUrl } = await this.pickPhoto('dni-dorso.jpg');
      this.x_img_dni_dorso = file;
      this.preview_img_dni_dorso.set(dataUrl);
    } catch (err) {
      console.error('Error al capturar DNI dorso:', err);
    }
  }

  // ------------------ Eliminar imágenes ------------------

  nombreImagenAEliminar = signal<string>('');
  private modalConfirmarEliminarInstance?: any;
  private accionEliminarPendiente: (() => Promise<void>) | null = null;

  private abrirConfirmarEliminarImagen(nombre: string, accion: () => Promise<void>): void {
    this.nombreImagenAEliminar.set(nombre);
    this.accionEliminarPendiente = accion;
    const modalEl = document.getElementById('modalConfirmarEliminarImagen');
    if (modalEl) {
      this.modalConfirmarEliminarInstance = new (bootstrap as any).Modal(modalEl);
      this.modalConfirmarEliminarInstance.show();
    }
  }

  async confirmarEliminarImagen(): Promise<void> {
    const accion = this.accionEliminarPendiente;
    this.accionEliminarPendiente = null;
    this.modalConfirmarEliminarInstance?.hide();
    if (accion) await accion();
  }

  private async ejecutarEliminarImagen(
    borrar: (id: string) => Observable<any>,
    previewSignal: WritableSignal<string>,
    limpiarArchivo: () => void
  ): Promise<void> {
    if (!this.id) return;
    await this._loadingService.run(async () => {
      try {
        await firstValueFrom(borrar(this.id!));
        previewSignal.set('');
        limpiarArchivo();
      } catch (e) {
        console.error('Error al eliminar la imagen:', e);
        this.error.set('No se pudo eliminar la imagen. Intentá nuevamente.');
      }
    });
  }

  eliminarImgDniFrente(): void {
    this.abrirConfirmarEliminarImagen('el DNI frente', () =>
      this.ejecutarEliminarImagen(
        (id) => this._usuarioService.deleteClienteImgDniFrente(id),
        this.preview_img_dni_frente,
        () => this.x_img_dni_frente = undefined
      )
    );
  }

  eliminarImgDniDorso(): void {
    this.abrirConfirmarEliminarImagen('el DNI dorso', () =>
      this.ejecutarEliminarImagen(
        (id) => this._usuarioService.deleteClienteImgDniDorso(id),
        this.preview_img_dni_dorso,
        () => this.x_img_dni_dorso = undefined
      )
    );
  }

  extraerBase64 = async ($event: any) => new Promise((resolve, reject) => {
    try {
      const unsafeImg = window.URL.createObjectURL($event);
      const image = this.sanitizer.bypassSecurityTrustUrl(unsafeImg);
      const reader = new FileReader();
      reader.readAsDataURL($event);
      reader.onload = () => {
        resolve({
          base: reader.result
        });
      };
      reader.onerror = error => {
        resolve({
          base: null
        });
      };
      return;
    } catch (e) {
      return null;
    }
  })

  toggleFieldTextType() {
    this.fieldTextType = !this.fieldTextType;
  }

  selectFilter(option: 'perfil' | 'detalle') {
    this.showList = option;
  }

  back(){
    this._location.back();
  }
}
